/**
 * Die Bildmarke des Entwicklers — animierter Pfadaufbau.
 *
 * KOPIE aus `pd-homepage` (dort `components/pd-logo/pd-logo.ts`). Sie liegt
 * dort als App-Komponente, nicht im geteilten Paket; sobald eine dritte App
 * sie will, gehoert sie nach pd-web-components hochgezogen statt ein drittes
 * Mal kopiert.
 *
 * Drei Aenderungen gegenueber dem Original, alle aus den Portal-Regeln:
 *
 *   1. Die Strichfarbe kommt aus einem Token statt fest aus Weiss. Im Fuss
 *      steht die Marke auf Petrol, da ist Weiss richtig — auf hellem Grund
 *      waere sie unsichtbar.
 *   2. Die Bewegung laeuft EINMAL beim ersten Sichtbarwerden, nicht bei jedem
 *      Seitenaufruf. Der Fuss steht auf jeder Seite; eine Animation, die bei
 *      jedem Routenwechsel neu losrennt, ist Gezappel. Dafuer derselbe Helfer
 *      wie bei den Balken (`beobachteWachsen`).
 *   3. `prefers-reduced-motion` schaltet sie ab — und dann werden die Pfade
 *      SOFORT VOLL gezeichnet. Das ist die Falle: Das Original setzt den
 *      Strichversatz in `firstUpdated` blind, ohne Animation bliebe die Marke
 *      unsichtbar.
 *
 * `isIcon` zeigt nur das Zeichen (P/D) ohne den Schriftzug.
 */

import { svg, LitElement, css } from "lit";
import { customElement, property } from "lit/decorators.js";

import { beobachteWachsen } from "../../animation/balken-wachsen.js";

@customElement("app-pd-logo")
export class AppPdLogo extends LitElement {
  @property({ type: String }) cssClass: string = "";

  /**
   * Quick add to use the svg element inside a slot for other custom elements (slot name reference).
   */
  @property({ type: String }) svgSlotName: string = "";

  @property({ type: Boolean }) isIcon: boolean = false;

  static override styles = [
    css`
      :host {
        display: inline-flex;
        height: 100%;
        flex: 1 1 auto;
      }

      :host([isIcon]) {
        flex: 0 1 10rem;
        cursor: pointer;
      }

      .logo-symbol-d,
      .logo-symbol-p {
        stroke: var(--portal-marke-strich, #fff);
        stroke-width: 4;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-miterlimit: 10;
        fill: none;
      }

      :host(.bewegt) .logo-symbol-d,
      :host(.bewegt) .logo-symbol-p {
        animation: dash 1.25s linear;
        animation-fill-mode: forwards;
      }

      .logo-text {
        fill: transparent;
      }

      :host(.bewegt) .logo-text {
        animation: text 0.75s linear;
        animation-delay: 0.75s;
        animation-fill-mode: forwards;
      }

      /* Ohne Bewegung steht die Marke sofort fertig da. */
      @media (prefers-reduced-motion: reduce) {
        .logo-text {
          fill: var(--portal-marke-strich, #fff);
        }
      }

      @keyframes dash {
        to {
          stroke-dashoffset: 0;
        }
      }
      @keyframes text {
        to {
          fill: var(--portal-marke-strich, #fff);
        }
      }
    `,
  ];

  /** Beendet die Beobachtung beim Abraeumen. */
  private _beenden?: () => void;

  override firstUpdated(): void {
    // Wer keine Bewegung will, bekommt die fertige Marke: KEIN Strichversatz,
    // sonst bliebe sie unsichtbar, weil die Animation nie laeuft.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const pfadD = this.shadowRoot?.querySelector(".logo-symbol-d") as SVGPathElement | null;
    const pfadP = this.shadowRoot?.querySelector(".logo-symbol-p") as SVGPathElement | null;
    const laenge = pfadD?.getTotalLength() || 0;
    if (!laenge) return;

    const versatz = `stroke-dasharray: ${laenge}; stroke-dashoffset: ${laenge}`;
    pfadD?.setAttribute("style", versatz);
    pfadP?.setAttribute("style", versatz);

    // Einmal, sobald die Marke wirklich zu sehen ist — nicht bei jedem Aufbau.
    this._beenden = beobachteWachsen(this, () => this.classList.add("bewegt"));
  }

  override disconnectedCallback(): void {
    this._beenden?.();
    this._beenden = undefined;
    super.disconnectedCallback();
  }

  override render() {
    return svg`<svg slot="${this.svgSlotName}" class="${this.cssClass}" viewBox="${this.isIcon ? "0 0 166.68 212.6" : "0 0 411.59 212.6"}">
      <g class="logo-symbol">
        <path class="logo-symbol-d" d="M123.9,80.5l3.6,0.1c6.7,0,12.2-5.5,12.2-12.2s-5.5-12.2-12.2-12.2c-6.7,0-12.2,5.5-12.2,12.2v75.8
          c0,15-12.2,27.2-27.2,27.2s-27.2-12.2-27.2-27.2c0-14.1,10.7-25.7,24.4-27.1V74.7c0-4.4-3.5-8-7.9-8c-4.4,0-8,3.6-8,8
          c0,4.4,3.6,8,8,8"/>
        <path class="logo-symbol-p" d="M91.7,132.1l-3.6-0.1c-6.7,0-12.2,5.5-12.2,12.2c0,6.7,5.5,12.2,12.2,12.2s12.2-5.5,12.2-12.2V68.4
          c0-15,12.2-27.2,27.2-27.2s27.2,12.2,27.2,27.2c0,14.1-10.8,25.7-24.5,27.1v42.1c0,4.4,3.6,8,8,8c4.4,0,8-3.6,8-8s-3.6-8-8-8"/>
      </g>
      ${
        this.isIcon
          ? ""
          : svg`
      <g class="logo-text">
        <path d="M190.82,77.19a9.65,9.65,0,0,1,0,13.75c-2.63,2.37-5.81,2.37-9.2,2.37h-2.37v10.14h-3.18v-22c0-2.07,0-4,1.72-5.46a6.28,6.28,0,0,1,4.47-1.21C185.23,74.82,188.19,74.82,190.82,77.19Zm-10.84,1c-.73.56-.73,1.5-.73,3.27V90.3h2.37c3.18,0,5.28,0,7-1.51a6.75,6.75,0,0,0,0-9.45c-1.68-1.51-3.57-1.51-6.32-1.51C181.1,77.83,180.5,77.83,180,78.22Z" />
        <path d="M207,83.12l-.9,2.71a5.9,5.9,0,0,0-2-.35A5,5,0,0,0,200.25,87c-1,1.2-1,3-1,5.72v10.7h-3V92.75c0-3.48,0-5.5,1.72-7.52a8,8,0,0,1,6.15-2.58A8.85,8.85,0,0,1,207,83.12Z" />
        <path d="M223.55,86.3c1.24,2.06,1.24,4.43,1.24,6.88s0,4.82-1.24,6.88a8.39,8.39,0,0,1-13.85,0c-1.24-2.06-1.24-4.43-1.24-6.88s0-4.82,1.24-6.88a8.39,8.39,0,0,1,13.85,0Zm-11.14,1.25c-.94,1.42-.94,3.44-.94,5.63s0,4.21.94,5.63a5.34,5.34,0,0,0,8.43,0c.94-1.42.94-3.44.94-5.63s0-4.21-.94-5.63a5.33,5.33,0,0,0-8.43,0Z" />
        <path d="M241.85,85.23c1.72,2,1.72,4,1.72,7.52v8.77c0,3,0,5.42-2,7.57a8.44,8.44,0,0,1-6.4,2.49,13.45,13.45,0,0,1-6.28-1.55l1.25-2.58a10.7,10.7,0,0,0,5,1.29,5.27,5.27,0,0,0,4.34-1.76c1-1.29,1-2.84,1-5.46v-.43a6.28,6.28,0,0,1-5.16,2.62,7.37,7.37,0,0,1-5.8-2.54c-1.94-2.32-1.94-5.15-1.94-8.08,0-2.66,0-5.67,2.07-8a8.56,8.56,0,0,1,12.12.13Zm-9.8,1.67c-1.38,1.55-1.38,3.74-1.38,6.19s0,4.77,1.29,6.28a5.2,5.2,0,0,0,7.31,0c1.29-1.51,1.29-3.87,1.29-7.05,0-2.28,0-4.09-1-5.29a5,5,0,0,0-3.83-1.55A4.83,4.83,0,0,0,232.05,86.9Z" />
        <path d="M258.09,83.12l-.9,2.71a5.9,5.9,0,0,0-2-.35A5,5,0,0,0,251.34,87c-1,1.2-1,3-1,5.72v10.7h-3V92.75c0-3.48,0-5.5,1.72-7.52a8,8,0,0,1,6.15-2.58A8.85,8.85,0,0,1,258.09,83.12Z" />
        <path d="M267.89,100.87a11.4,11.4,0,0,0,5.24-1.24l1.25,2.58a14.27,14.27,0,0,1-6.49,1.5,8.06,8.06,0,0,1-6.71-2.92c-1.63-2.24-1.63-4.82-1.63-7.61,0-3.35,0-5.33,1.55-7.48,1.37-1.94,3.95-3,7-3a8,8,0,0,1,5.16,1.59,5.88,5.88,0,0,1,2.15,4.34,5.09,5.09,0,0,1-2.15,4.21c-1.8,1.29-3.65,1.29-6.7,1.29h-4c0,1.12,0,3.66,1,5A5,5,0,0,0,267.89,100.87Zm3.39-14.48a5,5,0,0,0-3.18-.91,5.59,5.59,0,0,0-4.43,1.68c-1.07,1.25-1.07,3.4-1.07,4.26h4c2.7,0,3.91,0,4.81-.69a2.66,2.66,0,0,0,1-2.15A3.08,3.08,0,0,0,271.28,86.39Z" />
        <path d="M290.85,86.69a12.18,12.18,0,0,0-5.16-1.21,4.68,4.68,0,0,0-2.58.61,2.47,2.47,0,0,0-1.25,2.14,2.68,2.68,0,0,0,1.08,2.11,7.44,7.44,0,0,0,3.31,1.25,11,11,0,0,1,4.17,1.59A5.74,5.74,0,0,1,293,97.86a5.46,5.46,0,0,1-2.32,4.48,8.29,8.29,0,0,1-4.94,1.37,16.35,16.35,0,0,1-6.92-1.5l1.2-2.58a13.78,13.78,0,0,0,5.72,1.24,5.2,5.2,0,0,0,3.14-.81,2.72,2.72,0,0,0,1.12-2.2,2.92,2.92,0,0,0-1.21-2.32,8.67,8.67,0,0,0-3.39-1.24,10.32,10.32,0,0,1-4.22-1.68,5.58,5.58,0,0,1-2.28-4.39,5.26,5.26,0,0,1,2.33-4.34,7.71,7.71,0,0,1,4.51-1.24A15,15,0,0,1,292,84.06Z" />
        <path d="M307.39,86.69a12.15,12.15,0,0,0-5.16-1.21,4.7,4.7,0,0,0-2.58.61,2.46,2.46,0,0,0-1.24,2.14,2.68,2.68,0,0,0,1.07,2.11,7.44,7.44,0,0,0,3.31,1.25A11,11,0,0,1,307,93.18a5.75,5.75,0,0,1,2.54,4.68,5.48,5.48,0,0,1-2.32,4.48,8.35,8.35,0,0,1-4.95,1.37,16.35,16.35,0,0,1-6.92-1.5l1.2-2.58a13.81,13.81,0,0,0,5.72,1.24,5.2,5.2,0,0,0,3.14-.81,2.69,2.69,0,0,0,1.12-2.2,2.92,2.92,0,0,0-1.21-2.32,8.67,8.67,0,0,0-3.39-1.24,10.32,10.32,0,0,1-4.22-1.68,5.57,5.57,0,0,1-2.27-4.39,5.26,5.26,0,0,1,2.32-4.34,7.71,7.71,0,0,1,4.51-1.24,15,15,0,0,1,6.28,1.41Z" />
        <path d="M315.54,77.57a2,2,0,1,1-2-2A1.94,1.94,0,0,1,315.54,77.57ZM312,82.9h3v20.55h-3Z" />
        <path d="M325.3,98.38a5.66,5.66,0,0,0,.69,2.06,1.18,1.18,0,0,0,.9.43,1.2,1.2,0,0,0,.9-.43,5.66,5.66,0,0,0,.69-2.06l3.74-15.48h3.1l-3.83,15.78c-.47,2-.69,3.1-1.89,4.09a4.38,4.38,0,0,1-5.42,0c-1.2-1-1.42-2.11-1.89-4.09L318.46,82.9h3.1Z" />
        <path d="M344.81,100.87a11.4,11.4,0,0,0,5.24-1.24l1.25,2.58a14.27,14.27,0,0,1-6.49,1.5,8.06,8.06,0,0,1-6.71-2.92c-1.63-2.24-1.63-4.82-1.63-7.61,0-3.35,0-5.33,1.55-7.48,1.37-1.94,4-3,7-3a8,8,0,0,1,5.16,1.59,5.88,5.88,0,0,1,2.15,4.34,5.09,5.09,0,0,1-2.15,4.21c-1.8,1.29-3.65,1.29-6.7,1.29h-4c0,1.12,0,3.66,1,5A5,5,0,0,0,344.81,100.87Zm3.39-14.48a5,5,0,0,0-3.18-.91,5.59,5.59,0,0,0-4.43,1.68c-1.07,1.25-1.07,3.4-1.07,4.26h4c2.7,0,3.91,0,4.81-.69a2.66,2.66,0,0,0,1-2.15A3.08,3.08,0,0,0,348.2,86.39Z" />
        <path d="M182.74,122.14v-7h2v12.44c0,2.15,0,3.58-1,4.93a4.87,4.87,0,0,1-4,1.89,5.64,5.64,0,0,1-4-1.57c-1.6-1.64-1.6-3.65-1.6-5.4,0-1.94,0-3.84,1.29-5.38a4.89,4.89,0,0,1,3.87-1.7A4.2,4.2,0,0,1,182.74,122.14Zm-.86,1.15a3.48,3.48,0,0,0-4.88,0c-.86,1-.86,2.58-.86,4.18s0,3,1,4.05a3.48,3.48,0,0,0,2.58,1,3,3,0,0,0,2.32-1c.69-.84.69-2.07.69-3.59C182.74,125.87,182.74,124.29,181.88,123.29Z" />
        <path d="M193.17,132.55a7.68,7.68,0,0,0,3.5-.83l.83,1.72a9.49,9.49,0,0,1-4.33,1,5.36,5.36,0,0,1-4.47-1.95c-1.09-1.49-1.09-3.21-1.09-5.07,0-2.24,0-3.56,1-5a5.6,5.6,0,0,1,4.67-2,5.34,5.34,0,0,1,3.44,1.07,3.93,3.93,0,0,1,1.44,2.89,3.39,3.39,0,0,1-1.44,2.81c-1.2.86-2.43.86-4.47.86h-2.64a6.26,6.26,0,0,0,.66,3.32A3.3,3.3,0,0,0,193.17,132.55Zm2.26-9.66a3.32,3.32,0,0,0-2.12-.6,3.73,3.73,0,0,0-2.95,1.11,4.59,4.59,0,0,0-.72,2.84h2.64c1.81,0,2.61,0,3.21-.46a1.77,1.77,0,0,0,.69-1.43A2.09,2.09,0,0,0,195.43,122.89Z" />
        <path d="M204.32,130.89a3.8,3.8,0,0,0,.46,1.37.77.77,0,0,0,1.2,0,3.8,3.8,0,0,0,.46-1.37l2.5-10.32H211l-2.55,10.52c-.32,1.32-.46,2.06-1.26,2.72a2.92,2.92,0,0,1-3.62,0c-.8-.66-.94-1.4-1.26-2.72l-2.55-10.52h2.07Z" />
        <path d="M218,132.55a7.62,7.62,0,0,0,3.5-.83l.83,1.72a9.46,9.46,0,0,1-4.33,1,5.39,5.39,0,0,1-4.47-1.95c-1.09-1.49-1.09-3.21-1.09-5.07,0-2.24,0-3.56,1-5a5.61,5.61,0,0,1,4.68-2,5.35,5.35,0,0,1,3.44,1.07,3.92,3.92,0,0,1,1.43,2.89,3.41,3.41,0,0,1-1.43,2.81c-1.21.86-2.44.86-4.48.86h-2.63a6.26,6.26,0,0,0,.66,3.32A3.28,3.28,0,0,0,218,132.55Zm2.27-9.66a3.34,3.34,0,0,0-2.12-.6,3.75,3.75,0,0,0-3,1.11,4.65,4.65,0,0,0-.71,2.84h2.63c1.81,0,2.61,0,3.22-.46a1.76,1.76,0,0,0,.68-1.43A2,2,0,0,0,220.26,122.89Z" />
        <path d="M228,115.15v19.12h-2V115.15Z" />
        <path d="M241.24,122.83a8.56,8.56,0,0,1,.83,4.59,8.53,8.53,0,0,1-.83,4.58,5.58,5.58,0,0,1-9.23,0,8.53,8.53,0,0,1-.83-4.58,8.56,8.56,0,0,1,.83-4.59,5.58,5.58,0,0,1,9.23,0Zm-7.42.83a7,7,0,0,0-.63,3.76,6.9,6.9,0,0,0,.63,3.75,3.55,3.55,0,0,0,5.62,0,6.9,6.9,0,0,0,.63-3.75,7,7,0,0,0-.63-3.76,3.57,3.57,0,0,0-5.62,0Z" />
        <path d="M254.17,122c1.37,1.55,1.37,3.55,1.37,5.33,0,2,0,3.84-1.29,5.39a4.92,4.92,0,0,1-3.87,1.69,4.17,4.17,0,0,1-3.43-1.75v6.83h-2V127.13c0-2.32,0-3.67,1.14-5a5.37,5.37,0,0,1,4.1-1.72A5.28,5.28,0,0,1,254.17,122Zm-6.54,1.29c-.68.8-.68,2-.68,3.52,0,2.13,0,3.7.85,4.71a3.48,3.48,0,0,0,4.88,0c.86-1,.86-2.58.86-4.19s0-3.1-.92-4.13a3.22,3.22,0,0,0-2.44-.94A3.32,3.32,0,0,0,247.63,123.32Z" />
        <path d="M266.87,122.4a4.15,4.15,0,0,1,3.72-2,4.73,4.73,0,0,1,3.3,1.24c1.43,1.37,1.43,3.15,1.43,5.3v7.34h-2v-7.34c0-1.89,0-3.18-.86-4a3,3,0,0,0-3.73,0c-.86.8-.86,2.09-.86,4v7.34h-2v-7.34c0-1.89,0-3.18-.86-4a3,3,0,0,0-3.72,0c-.86.8-.86,2.09-.86,4v7.34h-2v-7.34c0-2.15,0-3.93,1.43-5.3a4.74,4.74,0,0,1,3.3-1.24A4.18,4.18,0,0,1,266.87,122.4Z" />
        <path d="M283.75,132.55a7.65,7.65,0,0,0,3.5-.83l.83,1.72a9.49,9.49,0,0,1-4.33,1,5.38,5.38,0,0,1-4.47-1.95c-1.09-1.49-1.09-3.21-1.09-5.07,0-2.24,0-3.56,1-5a5.6,5.6,0,0,1,4.67-2,5.34,5.34,0,0,1,3.44,1.07,3.93,3.93,0,0,1,1.44,2.89,3.39,3.39,0,0,1-1.44,2.81c-1.2.86-2.43.86-4.47.86h-2.64a6.26,6.26,0,0,0,.66,3.32A3.32,3.32,0,0,0,283.75,132.55Zm2.27-9.66a3.37,3.37,0,0,0-2.13-.6,3.73,3.73,0,0,0-2.95,1.11,4.59,4.59,0,0,0-.72,2.84h2.64c1.81,0,2.61,0,3.21-.46a1.74,1.74,0,0,0,.69-1.43A2.08,2.08,0,0,0,286,122.89Z" />
        <path d="M300.18,121.86c1.34,1.4,1.34,3.06,1.34,5.07v7.34h-2v-7.34c0-1.78,0-3-.83-3.81a3.28,3.28,0,0,0-4.36,0c-.83.83-.83,2-.83,3.81v7.34h-2v-7.34c0-2,0-3.67,1.35-5.07a5.32,5.32,0,0,1,7.34,0Z" />
        <path d="M307.23,132a1.64,1.64,0,0,0,1.34.57,4.19,4.19,0,0,0,1.87-.55l.86,1.7a6,6,0,0,1-2.73.74,3.7,3.7,0,0,1-2.95-1.32c-.8-1-.8-2.09-.8-3.72V117.33h2v3.24h3.44v1.77h-3.44v7.06C306.83,130.94,306.83,131.46,307.23,132Z" />
      </g>`
      }
    </svg>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-pd-logo": AppPdLogo;
  }
}
